import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { cn } from "@nexus/ui";
import type {
  AskNexusAnswerBlock,
  AskNexusCard,
  AskNexusComparisonTableCard,
  AskNexusDataCard,
  AskNexusKpiTilesCard,
} from "@/features/ask-nexus/api/ask-nexus.api";
import {
  cardNotes,
  cardPeriod,
  cardTitle,
  cellText,
  columnLabel,
  formatCardValue,
  provenanceLines,
  rowLabel,
} from "@/features/ask-nexus/lib/card-format";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";

type UnavailableReason = Extract<AskNexusAnswerBlock, { type: "card_unavailable" }>["reason"];

const UNAVAILABLE_TEXT: Record<UnavailableReason, { key: string; fallback: string }> = {
  invalid: { key: "askNexus.cards.unavailable.invalid", fallback: "A card in this answer isn't shown because it wasn't written correctly." },
  not_found: { key: "askNexus.cards.unavailable.notFound", fallback: "A card in this answer isn't shown because its data isn't in this answer's tool results." },
  no_values: { key: "askNexus.cards.unavailable.noValues", fallback: "A card in this answer isn't shown because none of its values were returned." },
  too_many: { key: "askNexus.cards.unavailable.tooMany", fallback: "A card in this answer isn't shown because an answer shows at most four cards." },
};

export function AskNexusKpiTiles({ card }: { card: AskNexusKpiTilesCard }) {
  const { t, i18n } = useTranslation();
  return (
    <CardFrame card={card} testId="ask-nexus-card-kpi">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {card.tiles.map((tile) => (
          <div key={tile.metric} className="rounded-xl bg-(--an-inset) px-3 py-2.5">
            <p className="truncate text-[10px] font-medium tracking-[0.09em] text-(--an-ink-2) uppercase">{tile.label}</p>
            <p className="mt-1 text-xl leading-tight font-medium tabular-nums">{formatCardValue(tile.value, tile.format, i18n.language)}</p>
            <p className="mt-0.5 text-[11px] text-(--an-ink-2) tabular-nums">
              {t("askNexus.cards.previous", "Previous period: {{value}}", { value: formatCardValue(tile.previousValue, tile.format, i18n.language) })}
            </p>
          </div>
        ))}
      </div>
    </CardFrame>
  );
}

export function AskNexusComparisonTable({ card }: { card: AskNexusComparisonTableCard }) {
  const { t, i18n } = useTranslation();
  return (
    <CardFrame card={card} testId="ask-nexus-card-table">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs tabular-nums">
          <thead>
            <tr className="border-b border-(--an-hair-strong)">
              <th className="pr-2 pb-2" />
              {card.columns.map((column, index) => (
                <th key={index} className="pr-2 pb-2 text-right font-medium whitespace-nowrap text-(--an-ink-2)">
                  {columnLabel(t, column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {card.rows.map((row) => (
              <tr key={row.label} className="border-b border-(--an-hair) last:border-b-0">
                <td className="py-[9px] pr-2 text-left">{rowLabel(card, row.label)}</td>
                {row.cells.map((cell, index) => (
                  <td key={index} className={cn("py-[9px] pr-2 text-right whitespace-nowrap", cell.note && "text-(--an-ink-3)")}>
                    {cellText(t, cell, i18n.language)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardFrame>
  );
}

/** A card the agent asked for that Nexus could not back with this answer's data. */
export function AskNexusCardUnavailable({ reason }: { reason: UnavailableReason }) {
  const { t } = useTranslation();
  return (
    <p className="flex items-start gap-2 rounded-xl bg-(--an-inset) px-3 py-2.5 text-xs text-(--an-ink-2)" data-testid="ask-nexus-card-unavailable">
      <Info className="mt-px size-3.5 flex-none" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      {t(UNAVAILABLE_TEXT[reason].key, UNAVAILABLE_TEXT[reason].fallback)}
    </p>
  );
}

/** A card built from tool data: its title, period, content, notes and provenance footer. */
export function CardFrame({
  card,
  testId,
  children,
}: {
  card: AskNexusDataCard;
  testId: string;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const notes = cardNotes(t, card);
  const period = cardPeriod(card.provenance);
  return (
    <figure className="min-w-0 rounded-2xl border border-(--an-hair) bg-(--an-field) p-4" data-testid={testId}>
      <figcaption className="mb-3">
        <p className="text-[13px] font-medium">{cardTitle(t, card)}</p>
        {period ? <p className="text-xs text-(--an-ink-2) tabular-nums">{period}</p> : null}
      </figcaption>
      {children}
      {notes.length > 0 ? (
        <ul className="mt-2.5 space-y-0.5 text-[11px] text-(--an-ink-2)">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
      <footer className="mt-3 border-t border-(--an-hair) pt-2.5 text-[11px] leading-normal text-(--an-ink-2)" data-testid="ask-nexus-card-provenance">
        {provenanceLines(t, card.provenance, i18n.language).map((line, index) => (
          <p key={index} className={index > 0 ? "mt-0.5" : undefined}>
            {line}
          </p>
        ))}
      </footer>
    </figure>
  );
}
